import { ChangeDetectionStrategy, Component } from '@angular/core';
import { getUuidV4, ModalClass } from '@evorait/pace-core';
import { faCheck, faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Observable, combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { addParticipantCopy, checkAllParticipants, removeParticipant } from '../../store/actions/form.actions';
import {
    getFormParticipantCopies,
    getFormParticipantsSelectedInParticipantCopiesModal,
    getFormParticipantsWithoutCopies,
} from '../../store/selectors/form.selector';
import { FormsState } from '../../store/state/pace-forms.state';
import { FormParticipantCopies } from '../../types/form-participant-copies';
import { FormsConstants } from '../../types/forms-constants';
import { ParticipantCopy } from '../../types/participant-copy';
import { AddParticipantComponent } from './add-participant/add-participant.component';
import { CopyChecklistSignatureComponent } from './copy-checklist-signature/copy-checklist-signature.component';

@Component({
    selector: 'pace-participant-copies',
    templateUrl: './participant-copies.component.html',
    styleUrls: ['./participant-copies.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParticipantCopiesComponent {
    public formParticipantCopies$: Observable<FormParticipantCopies>;
    public formParticipantsWithoutCopies$: Observable<ParticipantCopy[]>;
    public participantCopies$: Observable<ParticipantCopy[]>;
    public selectedParticipants$: Observable<ParticipantCopy[]>;
    public selectedAllParticipants$: Observable<boolean>;

    public faCheck = faCheck;
    public faPencil = faPencilAlt;
    public constructor(
        private readonly store: Store<FormsState>,
        private readonly modalService: BsModalService,
        public readonly modalInstance: BsModalRef,
    ) {
        this.formParticipantCopies$ = this.store.select(getFormParticipantCopies);
        this.formParticipantsWithoutCopies$ = this.store.select(getFormParticipantsWithoutCopies);
        this.participantCopies$ = this.formParticipantCopies$.pipe(
            map((formParticipantCopies) =>
                Object.values(formParticipantCopies)
                    .filter((copy) => copy.id !== FormsConstants.MASTER_PARTICIPANT_ID)
                    .sort((a, b) => a.sortIndex - b.sortIndex),
            ),
        );
        this.selectedParticipants$ = this.store.select(getFormParticipantsSelectedInParticipantCopiesModal);

        this.selectedAllParticipants$ = combineLatest([
            this.formParticipantsWithoutCopies$,
            this.selectedParticipants$,
        ]).pipe(
            map(
                ([participantsWithoutCopies, selectedParticipants]) =>
                    participantsWithoutCopies.length === selectedParticipants.length &&
                    participantsWithoutCopies.length > 0,
            ),
        );
    }

    public onAllSelected(): void {
        this.store.dispatch(checkAllParticipants());
    }

    public copyChecklist(): void {
        this.modalService.show(CopyChecklistSignatureComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            ariaLabelledBy: 'copy-checklist-signature-modal-title',
        });

        this.modalService.onHide
            .pipe(take(1))
            .subscribe((reason: 'Created checklists' | 'backdrop-click' | 'esc' | null) => {
                // if the modal was closed without saving, we need to delete the participant again because it was not saved
                if (reason === 'Created checklists') {
                    this.modalInstance.hide();
                }
            });
    }

    public addParticipant(): void {
        const newParticipantId = getUuidV4();
        this.store.dispatch(addParticipantCopy({ id: newParticipantId }));

        this.modalService.show(AddParticipantComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            ariaLabelledBy: 'add-participant-modal-title',
        });

        this.modalService.onHide
            .pipe(take(1))
            .subscribe((reason: 'Participant saved' | 'backdrop-click' | 'esc' | null) => {
                // if the modal was closed without saving, we need to delete the participant again because it was not saved
                if (reason !== 'Participant saved') {
                    this.store.dispatch(removeParticipant({ participant: newParticipantId }));
                }
            });
    }
}
